import { DataSet } from '../design/charts/types';
import { DataRows, DataVizType, GenericWarning, Params, TimeFrame } from '../types';
import {
  MAX_DOUBLE_LINE_LEGEND_LENGTH,
  MAX_LEGEND_LENGTH,
  SHARED_BRAND_ASTERIX,
  SHARED_BRAND_TOTAL,
  MAX_COLUMNS_PER_SLIDE,
} from './constants';

export const getGlobalWarningMessages = (globalWarnings: GenericWarning[] = []): [string, string][] => {
  return globalWarnings.map(({ message }, index: number) =>
    globalWarnings.length > 1 ? [`⚠️ Alert-${index + 1}`, message] : [`⚠️ Alert`, message]
  );
};

export const createChartRef =
  (
    index: number,
    chartDataLength: number,
    setExportRef: React.Dispatch<React.SetStateAction<number>>,
    chartSliceRefs: React.MutableRefObject<HTMLDivElement | HTMLDivElement[]>
  ) =>
  (element: HTMLDivElement) => {
    if (chartDataLength === index + 1) {
      setExportRef(index + 1);
    }
    return (chartSliceRefs.current[index] = element);
  };

export const getDisplayAs = (componentSettings: Params): DataVizType => {
  if (componentSettings.dataViz?.length) {
    return componentSettings.dataViz[0] as DataVizType;
  }
  return DataVizType.chart;
};

export const shouldDisableChart = (dataSet: DataSet[]) => {
  const singleLineLegendLength = dataSet.length;

  // disable chart if too many single line legend items
  if (singleLineLegendLength > MAX_LEGEND_LENGTH) {
    return true;
  }

  const multiLineLegendLength = dataSet.reduce((acc, item) => {
    return acc + (item.subLabel ? 1 : 0);
  }, 0);

  if (multiLineLegendLength > MAX_DOUBLE_LINE_LEGEND_LENGTH) {
    return true;
  }

  return false;
};

export const formatSharedBrandsTotal = (brandName: string): string => {
  if (brandName.endsWith(SHARED_BRAND_ASTERIX)) {
    return brandName.replace(SHARED_BRAND_ASTERIX, SHARED_BRAND_TOTAL);
  }
  return brandName;
};

export const addTotalToMatchingSharedBrand = (value: string, label: string, sharedBrands: string[]) => {
  const matchingItem = sharedBrands?.find(brand => brand === value);
  if (matchingItem) {
    return label + SHARED_BRAND_TOTAL;
  }
  return label;
};

export const formatChartSharedBrands = (
  xColumnName: string,
  xData: {
    data: DataRows;
    timeFrame: TimeFrame;
  },
  sharedBrands: string[]
): void => {
  if (xColumnName === 'brand_name' && sharedBrands) {
    xData.data.forEach(row => {
      const label = row.values['brand_description'] as string;
      row.values['brand_description'] = addTotalToMatchingSharedBrand(label, label, sharedBrands);
    });
  }
};

/**
 * Calculates the optimal slice size for chart data based on the number of data sets and data labels(Comparison period).
 *
 * The function determines how many data points should be included in each chart slice,
 * ensuring that the total number of columns per slide does not exceed `MAX_COLUMNS_PER_SLIDE`.
 *
 * @param dataSetLength - The number of data sets to be displayed in the chart.
 * @param dataLabelsLength - The number of labels associated with the data sets.
 * @returns The calculated slice size for the chart.
 */
export const getChartSliceSize = (dataSetLength: number, dataLabelsLength: number): number => {
  const maxSlides = Math.ceil(((dataLabelsLength || 1) * (dataSetLength || 1)) / MAX_COLUMNS_PER_SLIDE);
  return Math.ceil((dataSetLength || 1) / maxSlides);
};
